import axios from 'axios';
import { IRemote } from './interface';
import { chat } from './chat';
import { game } from './game-events';
import { vote } from './vote-events';
import { avatar } from './avatar-events';
import { initializeAmplify, publishEvent, extractGameIdentifier, extractSessionId } from '../utils/amplify-config';


const API_BASE_URL = process.env.API_ENDPOINT;


// Initialize Amplify Events on module load
try {
  initializeAmplify();
} catch (error) {
  console.error('Failed to initialize Amplify:', error);
  console.warn('Amplify Events will not be available. Check RTC_ENDPOINT environment variable.');
}

// Wrapper for error handling
const handleRequest = async (request: Promise<any>) => {
  try {
    const response = await request;
    return response.data;
  } catch (error: any) {
    console.error('API Error:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Unified remote interface combining Amplify Events and REST API
 * 
 * Real-time subscriptions (onXxxSnapshot) → Amplify Events
 * Commands and mutations (saveXxx, setXxx, etc.) → REST API + AppSync Events publishing
 */
export const remote: IRemote = {
  // ===== REAL-TIME SUBSCRIPTIONS (Amplify Events) =====

  onGameSnapshot: game.onGameSnapshot.bind(game),
  onVoteSnapshot: vote.onVoteSnapshot.bind(vote),
  onAvatarSnapshot: avatar.onAvatarSnapshot.bind(avatar),
  onMeetingRoomSnapshot: game.onMeetingRoomSnapshot.bind(game),

  // Note: Chat is not included in IRemote interface, but available via import

  // ===== GAME COMMANDS (REST API + AppSync Events) =====

  getGameState: game.getGameState.bind(game),
  startGame: game.startGame.bind(game),
  startGamePhase: game.startGamePhase.bind(game),
  setPlayers: game.setPlayers.bind(game),
  openChat: game.openChat.bind(game),
  closeChat: game.closeChat.bind(game),
  setRoleMeetingRoom: game.setRoleMeetingRoom.bind(game),

  // ===== VOTE COMMANDS (REST API + AppSync Events) =====

  saveVote: vote.saveVote.bind(vote),
  saveVotesResults: vote.saveVotesResults.bind(vote),

  // ===== AVATAR COMMANDS (REST API + AppSync Events) =====

  saveAvatar: avatar.saveAvatar.bind(avatar),

  // ===== LOCATIONS & TODOS (REST API + AppSync Events) =====

  async syncLocations(gameRecordId, playerId, locations): Promise<void> {
    // 1. Execute REST API call
    await handleRequest(
      axios.post(`${API_BASE_URL}/locations`, { gameRecordId, playerId, locations })
    );

    // 2. Publish event to AppSync for game channel
    const gameIdentifier = extractGameIdentifier(gameRecordId);
    await publishEvent(`/game/${gameIdentifier}/locations`, {
      action: 'locations-synced',
      gameRecordId,
      playerId,
      locations,
    });

    // 3. Publish incremental update to session channel (for debrief)
    // Debrief will merge this with existing locations
    const sessionId = extractSessionId(gameRecordId);
    await publishEvent(`/session/${sessionId}`, {
      type: 'location-update',
      data: {
        gameRecordId,
        playerId,
        ...locations,
      },
    });
  },

  syncToDos(gameRecordId: string, playerId: string, toDos: any): Promise<void> {
    // Note: ToDos don't need real-time updates, only REST API call
    return handleRequest(
      axios.post(`${API_BASE_URL}/todos`, { gameRecordId, playerId, toDos })
    );
  },
};

// Export chat separately since it's not part of IRemote
export { chat };